/**
 * Role Management Page - User Access Management Module
 * Created: 2024-11-12
 * Purpose: Manage custom roles and permissions
 * Part of: User Access Management Module Implementation - Phase 4.2
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Fab,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  Badge,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Security as SecurityIcon,
  Search as SearchIcon,
  FileCopy as FileCopyIcon,
  Close as CloseIcon,
  AdminPanelSettings as AdminIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { customRolesAPI, permissionsAPI } from '../services/api';
import PermissionMatrixDialog from '../components/RoleManagement/PermissionMatrixDialog';

// ============================================================================
// INTERFACES
// ============================================================================

interface CustomRole {
  id: number;
  account_id: number;
  name: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  permission_count: number;
  user_count: number;
  created_at: string;
  updated_at: string;
}

interface Permission {
  id: number;
  resource: string;
  action: string;
  display_name: string;
  description: string;
  category: string;
  permission_string: string;
}

interface PermissionCategory {
  category: string;
  category_display_name: string;
  permissions: Permission[];
}

interface RoleFormData {
  name: string;
  description: string;
  is_active: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// ============================================================================
// TAB PANEL COMPONENT
// ============================================================================

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`role-tabpanel-${index}`}
      aria-labelledby={`role-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const RoleManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State
  const [roles, setRoles] = useState<CustomRole[]>([]);
  const [permissionsGrouped, setPermissionsGrouped] = useState<PermissionCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPermissionDialog, setOpenPermissionDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState<CustomRole | null>(null);
  const [editingRole, setEditingRole] = useState<CustomRole | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<CustomRole | null>(null);
  const [tabValue, setTabValue] = useState(0);

  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    is_active: true
  });

  // ============================================================================
  // FETCH DATA
  // ============================================================================

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await customRolesAPI.getRoles();
      setRoles(data);
    } catch (error: any) {
      console.error('Error fetching roles:', error);
      // Added: 2024-11-12 - Better error handling for 403 Forbidden
      if (error.response?.status === 403) {
        setSnackbar({
          open: true,
          message: 'Access Restricted: You do not have permission to view roles. Please contact your administrator.',
          severity: 'error'
        });
      } else {
        setSnackbar({
          open: true,
          message: formatErrorMessage(error) || 'Failed to fetch roles',
          severity: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const data = await permissionsAPI.getPermissionsGrouped();
      setPermissionsGrouped(data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  // ============================================================================
  // ERROR FORMATTING
  // ============================================================================

  const formatErrorMessage = (error: any): string => {
    if (error.response?.data) {
      if (Array.isArray(error.response.data.detail)) {
        return error.response.data.detail
          .map((err: any) => err.msg || JSON.stringify(err))
          .join(', ');
      }
      if (typeof error.response.data.detail === 'string') {
        return error.response.data.detail;
      }
      if (error.response.data.message) {
        return error.response.data.message;
      }
    }
    return error.message || 'An unknown error occurred';
  };

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  const handleCreateRole = async () => {
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        is_active: formData.is_active
      };

      await customRolesAPI.createRole(payload);
      setSnackbar({ open: true, message: 'Role created successfully', severity: 'success' });
      handleCloseDialog();
      fetchRoles();
    } catch (error: any) {
      console.error('Error creating role:', error);
      setSnackbar({
        open: true,
        message: formatErrorMessage(error) || 'Failed to create role',
        severity: 'error'
      });
    }
  };

  const handleUpdateRole = async () => {
    if (!editingRole) return;

    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        is_active: formData.is_active
      };

      await customRolesAPI.updateRole(editingRole.id, payload);
      setSnackbar({ open: true, message: 'Role updated successfully', severity: 'success' });
      handleCloseDialog();
      fetchRoles();
    } catch (error: any) {
      console.error('Error updating role:', error);
      setSnackbar({
        open: true,
        message: formatErrorMessage(error) || 'Failed to update role',
        severity: 'error'
      });
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;

    try {
      await customRolesAPI.deleteRole(roleToDelete.id);
      setSnackbar({ open: true, message: 'Role deleted successfully', severity: 'success' });
      setDeleteConfirmOpen(false);
      setRoleToDelete(null);
      fetchRoles();
    } catch (error: any) {
      console.error('Error deleting role:', error);
      setSnackbar({
        open: true,
        message: formatErrorMessage(error) || 'Failed to delete role',
        severity: 'error'
      });
    }
  };

  const handleCloneRole = async (role: CustomRole) => {
    try {
      const newName = `${role.name} (Copy)`;
      await customRolesAPI.cloneRole(role.id, newName);
      setSnackbar({ open: true, message: 'Role cloned successfully', severity: 'success' });
      fetchRoles();
    } catch (error: any) {
      console.error('Error cloning role:', error);
      setSnackbar({
        open: true,
        message: formatErrorMessage(error) || 'Failed to clone role',
        severity: 'error'
      });
    }
  };

  // ============================================================================
  // DIALOG HANDLERS
  // ============================================================================

  const handleOpenCreateDialog = () => {
    setFormData({
      name: '',
      description: '',
      is_active: true
    });
    setEditingRole(null);
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (role: CustomRole) => {
    setFormData({
      name: role.name,
      description: role.description || '',
      is_active: role.is_active
    });
    setEditingRole(role);
    setOpenDialog(true);
    handleCloseMenu();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      is_active: true
    });
  };

  const handleOpenPermissionDialog = (role: CustomRole) => {
    setSelectedRole(role);
    setOpenPermissionDialog(true);
    handleCloseMenu();
  };

  const handleClosePermissionDialog = () => {
    setOpenPermissionDialog(false);
    setSelectedRole(null);
    fetchRoles(); // Refresh to get updated permission counts
  };

  const handleOpenDeleteDialog = (role: CustomRole) => {
    setRoleToDelete(role);
    setDeleteConfirmOpen(true);
    handleCloseMenu();
  };

  // ============================================================================
  // MENU HANDLERS
  // ============================================================================

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, role: CustomRole) => {
    setAnchorEl(event.currentTarget);
    setSelectedRole(role);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // ============================================================================
  // FILTERING
  // ============================================================================

  const filteredRoles = roles.filter(role => {
    const matchesSearch = role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (role.description?.toLowerCase().includes(searchTerm.toLowerCase()) || false);
    return matchesSearch;
  });

  // ============================================================================
  // RENDER METHODS
  // ============================================================================

  const renderRoleCard = (role: CustomRole) => (
    <Grid item xs={12} sm={6} md={4} key={role.id}>
      <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box display="flex" alignItems="center">
              <SecurityIcon sx={{ mr: 1, color: role.is_default ? 'primary.main' : 'secondary.main' }} />
              <Typography variant="h6" component="div">
                {role.name}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => handleOpenMenu(e, role)}
              disabled={role.is_default}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: '40px' }}>
            {role.description || 'No description provided'}
          </Typography>

          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {role.is_default && (
              <Chip
                label="Default Role"
                size="small"
                color="primary"
                icon={<ShieldIcon />}
              />
            )}
            <Chip
              label={role.is_active ? 'Active' : 'Inactive'}
              size="small"
              color={role.is_active ? 'success' : 'default'}
              icon={role.is_active ? <CheckCircleIcon /> : undefined}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Permissions
              </Typography>
              <Typography variant="h6" color="primary">
                {role.permission_count}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Users
              </Typography>
              <Typography variant="h6" color="secondary">
                {role.user_count}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
        <Box p={2} pt={0}>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            onClick={() => handleOpenPermissionDialog(role)}
          >
            Manage Permissions
          </Button>
        </Box>
      </Card>
    </Grid>
  );

  const renderRoleTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Role Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Permissions</TableCell>
            <TableCell align="center">Users</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredRoles.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography variant="body2" color="text.secondary" py={4}>
                  No roles found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredRoles.map((role) => (
              <TableRow key={role.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <SecurityIcon sx={{ mr: 1, color: role.is_default ? 'primary.main' : 'secondary.main' }} />
                    <Box>
                      <Typography variant="body2" fontWeight={role.is_default ? 600 : 400}>
                        {role.name}
                      </Typography>
                      {role.is_default && (
                        <Chip label="Default" size="small" color="primary" sx={{ mt: 0.5 }} />
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {role.description || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={role.is_active ? 'Active' : 'Inactive'}
                    size="small"
                    color={role.is_active ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="center">
                  <Badge badgeContent={role.permission_count} color="primary">
                    <SecurityIcon />
                  </Badge>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{role.user_count}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Manage Permissions">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenPermissionDialog(role)}
                      color="primary"
                    >
                      <SecurityIcon />
                    </IconButton>
                  </Tooltip>
                  {!role.is_default && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenMenu(e, role)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Role Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage custom roles with granular permissions
          </Typography>
        </Box>
        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Create Role
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
        <Tab label="Roles" />
        <Tab label="About" />
      </Tabs>

      {/* Tab: Roles */}
      <TabPanel value={tabValue} index={0}>
        {/* Search Bar */}
        <Box mb={3}>
          <TextField
            fullWidth
            placeholder="Search roles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* Loading State */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Roles Grid */}
            <Grid container spacing={3}>
              {filteredRoles.map((role) => renderRoleCard(role))}
            </Grid>

            {filteredRoles.length === 0 && !loading && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="400px"
              >
                <SecurityIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No roles found
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  {searchTerm ? 'Try adjusting your search' : 'Create your first custom role to get started'}
                </Typography>
                {!searchTerm && (
                  <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
                    Create Role
                  </Button>
                )}
              </Box>
            )}
          </>
        )}
      </TabPanel>

      {/* Tab: About */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              About Role Management
            </Typography>
            <Typography variant="body2" paragraph>
              The Role Management system allows you to create custom roles with specific permissions
              tailored to your organization's needs.
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Default Roles
            </Typography>
            <Typography variant="body2" paragraph>
              Three default roles are provided: SUPER_ADMIN, ADMIN, and AGENT. These cannot be
              deleted or renamed but their permissions can be customized.
            </Typography>

            <Typography variant="subtitle2" gutterBottom>
              Custom Roles
            </Typography>
            <Typography variant="body2" paragraph>
              Create custom roles for specific use cases like "Support Manager", "Sales Rep",
              or "Billing Specialist" with precisely the permissions they need.
            </Typography>

            <Typography variant="subtitle2" gutterBottom>
              Permission Categories
            </Typography>
            <Typography variant="body2">
              Permissions are organized into {permissionsGrouped.length} categories covering all
              aspects of the platform including conversations, contacts, agents, reports, and more.
            </Typography>
          </CardContent>
        </Card>
      </TabPanel>

      {/* FAB for Mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={handleOpenCreateDialog}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRole ? 'Edit Role' : 'Create New Role'}
        </DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <TextField
              autoFocus
              fullWidth
              label="Role Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />
            <Box display="flex" alignItems="center" mt={2}>
              <Typography variant="body2" mr={2}>
                Status:
              </Typography>
              <Chip
                label={formData.is_active ? 'Active' : 'Inactive'}
                color={formData.is_active ? 'success' : 'default'}
                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                clickable
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={editingRole ? handleUpdateRole : handleCreateRole}
            variant="contained"
            disabled={!formData.name.trim()}
          >
            {editingRole ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permission Matrix Dialog */}
      {openPermissionDialog && selectedRole && (
        <PermissionMatrixDialog
          open={openPermissionDialog}
          role={selectedRole}
          onClose={handleClosePermissionDialog}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the role "{roleToDelete?.name}"?
          </Typography>
          {roleToDelete && roleToDelete.user_count > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This role is assigned to {roleToDelete.user_count} user(s).
              They will revert to their base role.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteRole} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => selectedRole && handleOpenEditDialog(selectedRole)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit Role
        </MenuItem>
        <MenuItem onClick={() => selectedRole && handleCloneRole(selectedRole)}>
          <FileCopyIcon fontSize="small" sx={{ mr: 1 }} />
          Clone Role
        </MenuItem>
        <MenuItem onClick={() => selectedRole && handleOpenPermissionDialog(selectedRole)}>
          <SecurityIcon fontSize="small" sx={{ mr: 1 }} />
          Manage Permissions
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => selectedRole && handleOpenDeleteDialog(selectedRole)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Role
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleManagementPage;
